import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Spinner,
    Alert,
    Modal,
    Button,
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useAuthenticatedAxios } from '@/shared/services/axiosInstance';
import { getCommunity } from '@/shared/services/communityApi';
import { getPost, deletePost, pinPost } from '@/shared/services/postApi';
import { checkMembership } from '@/shared/services/membershipApi';
import { formatRelativeTime, formatFullDate } from '@/shared/utils/dateUtils';
import { buildPostPermalink } from '@/shared/utils/shareUrls';
import SEO from '@/components/common/SEO';

import usePostEngagement from './hooks/usePostEngagement';
import PostCard from './components/PostCard';
import ShareButtons from './components/ShareButtons';
import ReportModal from './components/ReportModal';
import './Communities.css';

const PostDetailPage = () => {
    const { slug, postId } = useParams();
    const { user, isAdmin } = useAuth();
    const axiosInstance = useAuthenticatedAxios();
    const navigate = useNavigate();

    // State
    const [community, setCommunity] = useState(null);
    const [post, setPost] = useState(null);
    const [flashMessage, setFlashMessage] = useState(null);
    const [confirmDeletePost, setConfirmDeletePost] = useState(false);
    const [isModerator, setIsModerator] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fetchedForPostId, setFetchedForPostId] = useState(null);

    // Engagement hook — single-post adapter
    const handlePostReaction = useCallback(
        (id, counts) => setPost((prev) => ({ ...prev, ...counts })),
        []
    );

    const {
        engagements,
        fetchEngagements,
        handleLike: handleLikeEngagement,
        handleDislike: handleDislikeEngagement,
        handleBookmark: handleBookmarkEngagement,
    } = usePostEngagement(axiosInstance, { onReaction: handlePostReaction });

    // Fetch community and post data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [communityData, postData] = await Promise.all([
                getCommunity(slug),
                getPost(postId),
            ]);

            // Validate that the post belongs to this community
            if (postData.communityId !== communityData._id) {
                setError('Post not found in this community');
                return;
            }

            setCommunity(communityData);
            setPost(postData);
        } catch (err) {
            console.error('Error fetching post data:', err);
            if (err.response?.status === 404) {
                setError('Post not found');
            } else {
                setError('Failed to load post');
            }
        } finally {
            setLoading(false);
        }
    }, [slug, postId]);

    // Fetch membership status to determine moderator role
    const fetchMembershipStatus = useCallback(
        async (communityId) => {
            if (!user || !axiosInstance) return;
            try {
                const data = await checkMembership(axiosInstance, communityId);
                const role = data.membership?.role;
                setIsModerator(
                    isAdmin || role === 'moderator' || role === 'admin'
                );
            } catch (err) {
                console.error('Error checking membership:', err);
            }
        },
        [user, axiosInstance, isAdmin]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Fetch engagements once per post — post must match the current URL before
    // the gate can be consumed, preventing stale post data from a prior render
    useEffect(() => {
        if (
            post &&
            user &&
            post._id === postId &&
            fetchedForPostId !== postId
        ) {
            fetchEngagements([post]);
            setFetchedForPostId(postId);
        }
    }, [post, user, fetchEngagements, fetchedForPostId, postId]);

    useEffect(() => {
        if (community && user) {
            fetchMembershipStatus(community._id);
        }
    }, [community, user, fetchMembershipStatus]);

    // Handle like
    const handleLike = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to like posts.',
            });
            return;
        }
        handleLikeEngagement(postId);
    };

    // Handle dislike
    const handleDislike = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to dislike posts.',
            });
            return;
        }
        handleDislikeEngagement(postId);
    };

    // Handle bookmark
    const handleBookmark = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to bookmark posts.',
            });
            return;
        }
        handleBookmarkEngagement(postId);
    };

    // Build permalink for SEO and share buttons
    const permalink = buildPostPermalink(slug, postId);

    // Handle report
    const handleReportPost = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to report posts.',
            });
            return;
        }
        setShowReportModal(true);
    };

    // Handle report success
    const handleReportSuccess = () => {
        setPost((prev) => ({
            ...prev,
            reportCount: (prev.reportCount || 0) + 1,
        }));
    };

    // Handle delete post — opens confirm modal
    const handleDeletePost = () => {
        if (!user) {
            setFlashMessage({
                variant: 'warning',
                text: 'Please log in to delete posts.',
            });
            return;
        }
        setConfirmDeletePost(true);
    };

    // Perform the actual delete after confirmation
    const confirmDelete = async () => {
        setConfirmDeletePost(false);
        try {
            await deletePost(axiosInstance, postId);
            setFlashMessage({
                variant: 'success',
                text: 'Post deleted successfully.',
            });
            navigate(`/communities/${slug}`);
        } catch (err) {
            console.error('Error deleting post:', err);
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to delete post. Please try again.',
            });
        }
    };

    // Handle pin/unpin post
    const handlePin = async () => {
        try {
            const updatedPost = await pinPost(
                axiosInstance,
                postId,
                !post.pinned
            );
            setPost((prev) => ({
                ...prev,
                pinned: updatedPost.pinned,
                pinnedAt: updatedPost.pinnedAt,
            }));
            setFlashMessage({
                variant: 'success',
                text: updatedPost.pinned
                    ? 'Post pinned successfully.'
                    : 'Post unpinned successfully.',
            });
        } catch (err) {
            console.error('Error updating pin status:', err);
            const backendMessage = err.response?.data?.message;
            setFlashMessage({
                variant: 'danger',
                text:
                    backendMessage ||
                    'Failed to update pin status. Please try again.',
            });
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" />
                <span className="visually-hidden">Loading...</span>
            </div>
        );
    }

    // Error state
    if (error || !post || !community) {
        return (
            <section className="page-container">
                <Container className="py-5">
                    <Alert variant="warning">{error || 'Post not found'}</Alert>
                    <Link
                        to={`/communities/${slug}`}
                        className="btn btn-outline-primary"
                    >
                        Back to Community
                    </Link>
                </Container>
            </section>
        );
    }

    const seoTitle = post.title || 'Community Post';
    const seoDescription = post.content
        ? post.content.substring(0, 160).replace(/\n/g, ' ')
        : 'A post on Sustainability Hub';

    return (
        <section className="page-container">
            <SEO
                title={`${seoTitle}${community ? ` — ${community.name}` : ''}`}
                description={seoDescription}
                canonical={`/communities/${slug}/posts/${postId}`}
                image={post.mediaUrl || undefined}
                type="article"
            />
            <Container className="py-4">
                {/* Flash message (replaces window.alert) */}
                {flashMessage && (
                    <Alert
                        variant={flashMessage.variant}
                        dismissible
                        onClose={() => setFlashMessage(null)}
                    >
                        {flashMessage.text}
                    </Alert>
                )}

                {/* Back navigation */}
                <Row className="mb-3">
                    <Col>
                        <Link
                            to={`/communities/${slug}`}
                            className="text-decoration-none d-inline-flex align-items-center gap-2"
                        >
                            <i className="bi bi-arrow-left" />
                            Back to {community.name}
                        </Link>
                    </Col>
                </Row>

                {/* Post */}
                <Row className="justify-content-center">
                    <Col lg={10}>
                        <PostCard
                            post={{
                                postId: post._id,
                                communityName: community.name,
                                postType: post.postType,
                                title: post.title,
                                userName: post.authorName,
                                userProfilePic: post.authorProfilePic,
                                userDate: formatRelativeTime(post.createdAt),
                                userFullDate: formatFullDate(post.createdAt),
                                content: post.content,
                                mediaUrl: post.mediaUrl,
                                titleAsLink: false,
                                resourceUrl: post.resourceUrl,
                                resourceType: post.resourceType,
                                eventDate: post.eventDate,
                                eventEndDate: post.eventEndDate,
                                eventLocation: post.eventLocation,
                                eventRegistrationUrl: post.eventRegistrationUrl,
                                likes: post.likeCount,
                                dislikes: post.dislikeCount,
                                comments: post.commentCount,
                                shares: post.shareCount,
                                isPinned: post.pinned || false,
                                slug,
                                status: post.status,
                                deletedByRole: post.deletedByRole,
                            }}
                            engagement={{
                                reaction: engagements[postId]?.reaction || null,
                                isBookmarked:
                                    engagements[postId]?.isBookmarked || false,
                            }}
                            handlers={{
                                onLike: handleLike,
                                onDislike: handleDislike,
                                // onComment is an optional parent-notification
                                // hook. PostCard owns the reply-toggle state and
                                // renders ReplySection itself, so this page has
                                // nothing to do on toggle. Omitting the prop
                                // (rather than passing a silent no-op) keeps the
                                // wiring honest; PostCard guards for its absence.
                                onBookmark: handleBookmark,
                                onReport: handleReportPost,
                                onDelete: handleDeletePost,
                                onPin: handlePin,
                            }}
                            permissions={{
                                isModerator,
                                canDelete:
                                    !!user &&
                                    (post.isMine || isModerator || isAdmin),
                                user,
                            }}
                        />

                        {/* Share Buttons */}
                        <div className="mt-3 p-3 bg-white rounded shadow-sm">
                            <ShareButtons
                                url={permalink}
                                title={post.title}
                                communityName={community.name}
                            />
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Delete confirmation modal */}
            <Modal
                show={confirmDeletePost}
                onHide={() => setConfirmDeletePost(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this post?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setConfirmDeletePost(false)}
                    >
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Report Post Modal */}
            <ReportModal
                show={showReportModal}
                onHide={() => setShowReportModal(false)}
                postId={postId}
                communityId={community._id}
                axiosInstance={axiosInstance}
                onReportSuccess={handleReportSuccess}
            />
        </section>
    );
};

export default PostDetailPage;
